// Org switcher — popover dropdown in the TopBar.
//
// CHG-1525 / ITER-3644 (OrgSwitcher real-data) replaces the
// CHG-1167 MOCK_ORGS. Consumes useOrg() (now backed by React Query
// against GET /api/v1/organizations — admin-gated; donor-only
// sessions see the empty-state branch). The popover renders four
// states:
//
//   - Loading     → trigger pill shows "Loading organizations…" and
//                   is disabled.
//   - Error       → trigger pill shows "Couldn't load organizations";
//                   popover surfaces a Retry button.
//   - Empty       → trigger pill is hidden entirely (account has no
//                   admin orgs; nothing to switch between).
//   - Populated   → real orgs listed with active checkmark; click
//                   switches active org via useOrg().switchOrg.
//
// Per .forge/plans/v2-nav-shell.md.

import * as LucideIcons from "lucide-react";
import { type ComponentType, type RefObject } from "react";
import { useOrg } from "../../contexts/OrgContext";
// CHG-1562 / ITER-3678 — IA fix N3. "View as platform" item shows only
// when the account is a platform admin AND an org is active; reads
// useAuth().account.is_platform_admin alongside useOrg().
import { useAuth } from "../../contexts/AuthContext";
import { usePopover } from "./usePopover";

type IconCmp = ComponentType<{ size?: number; "aria-hidden"?: boolean | "true" }>;

export function OrgSwitcher() {
  const { orgs, org, switchOrg, clearActiveOrg, isLoading, isError, refetch } = useOrg();
  const { account } = useAuth();
  const popover = usePopover();

  const { Building2, ChevronDown, Check, Globe } = LucideIcons as unknown as {
    Building2: IconCmp;
    ChevronDown: IconCmp;
    Check: IconCmp;
    Globe: IconCmp;
  };

  // Empty state — donor-only account with no admin orgs, or admin
  // account whose membership was revoked. The Sidebar's visibleWhen
  // already hides admin nav for donors; the switcher staying empty
  // is the matching no-op in the top bar.
  if (!isLoading && !isError && orgs.length === 0) {
    return null;
  }

  // Trigger label varies with state so the chrome doesn't jump on
  // hydration.
  const triggerLabel = isLoading
    ? "Loading organizations…"
    : isError
    ? "Couldn't load organizations"
    : org?.name ?? "Select organization";

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        ref={popover.anchorRef as RefObject<HTMLButtonElement>}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={popover.open}
        onClick={popover.toggle}
        disabled={isLoading}
        data-testid="org-switcher-trigger"
        data-state={isLoading ? "loading" : isError ? "error" : "ready"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          background: "transparent",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-md, 8px)",
          color: isError ? "var(--color-danger, #b91c1c)" : "var(--color-text)",
          fontSize: 13,
          cursor: isLoading ? "wait" : "pointer",
          fontFamily: "inherit",
          opacity: isLoading ? 0.7 : 1,
        }}
      >
        <Building2 size={16} aria-hidden="true" />
        <span style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {triggerLabel}
        </span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {popover.open && (
        <div
          ref={popover.contentRef as RefObject<HTMLDivElement>}
          role="listbox"
          aria-label="Switch organization"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 900,
            minWidth: 280,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: "var(--radius-md, 8px)",
            boxShadow: "var(--shadow-medium)",
            padding: 6,
          }}
        >
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--color-text-muted)",
              padding: "6px 10px",
              fontWeight: 500,
            }}
          >
            Your organizations
          </div>

          {isError && (
            <div
              data-testid="org-switcher-error"
              style={{
                padding: "8px 10px",
                fontSize: 13,
                color: "var(--color-text-secondary)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span>Couldn&rsquo;t load your organizations.</span>
              <button
                type="button"
                onClick={() => {
                  void refetch();
                }}
                style={{
                  alignSelf: "flex-start",
                  padding: "4px 10px",
                  background: "var(--color-primary)",
                  color: "var(--color-on-primary, #fff)",
                  border: "none",
                  borderRadius: "var(--radius-sm, 6px)",
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* CHG-1562 / ITER-3678 — IA fix N3. "View as platform" affordance.
              Only renders when the account is a platform admin AND an org is
              currently active. Clicking clears activeOrgId via the new
              clearActiveOrg() context method; RoleAwareRedirect + Sidebar
              react on the next render and the user lands back at /platform
              with the Platform group as the only visible sidebar group.
              Pre-fix the only way out of an active-org state was devtools
              localStorage surgery — field-test v3 punch-list N4. */}
          {!isError && account?.is_platform_admin && org !== null && (
            <button
              type="button"
              role="option"
              aria-selected={false}
              data-testid="org-switcher-clear-active"
              onClick={() => {
                clearActiveOrg();
                popover.close();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "8px 10px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "var(--color-text)",
                fontSize: 13,
                borderRadius: "var(--radius-sm, 6px)",
                cursor: "pointer",
                fontFamily: "inherit",
                borderBottom: "1px solid var(--color-border-subtle)",
                marginBottom: 4,
                paddingBottom: 10,
              }}
            >
              <Globe size={14} aria-hidden="true" />
              <span style={{ flex: 1 }}>View as platform</span>
              <span
                style={{
                  fontSize: 11,
                  color: "var(--color-text-muted)",
                }}
              >
                clear org
              </span>
            </button>
          )}

          {!isError &&
            orgs.map((o) => {
              const selected = o.id === org?.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-testid={`org-switcher-option-${o.id}`}
                  onClick={() => {
                    switchOrg(o.id);
                    popover.close();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "8px 10px",
                    textAlign: "left",
                    border: "none",
                    background: selected ? "var(--color-surface-hover)" : "transparent",
                    color: "var(--color-text)",
                    fontSize: 13,
                    borderRadius: "var(--radius-sm, 6px)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  <Building2 size={14} aria-hidden="true" />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {o.name}
                  </span>
                  {selected && (
                    <Check
                      size={14}
                      aria-hidden="true"
                      data-testid={`org-switcher-active-check-${o.id}`}
                    />
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
